// ── AdminPanel.js ────────────────────────────────────────────────────
// Global admin space — reachable from TopBar regardless of being in a game.
// Tab 1 "Users": search any user, inspect their game slots, edit cheat
//   fields directly on a slot without needing to be inside that game.
// Tab 2 "Tickets": review bug reports / support tickets, change status.

import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where, doc, getDoc, setDoc, updateDoc, deleteDoc, orderBy, serverTimestamp } from 'firebase/firestore';
import { db, auth, isValidUsernameFormat } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useAccess } from '../useAccess';
import { TICKET_CATEGORY_LABELS, TICKET_STATUSES, TICKET_STATUS_MAP } from './ticketsDatabase';

const S = {
  anton: { fontFamily: "'Anton', sans-serif" },
  mono: { fontFamily: "'Monda', sans-serif" },
};

const gold = '#d4af37';
const dim = 'rgba(255,255,255,0.12)';
const faint = 'rgba(255,255,255,0.04)';

// ── Slot cheat editor (inline, reused per-slot) ──────────────────────
function SlotEditor({ slot, onSaved }) {
  const [budget, setBudget] = useState(slot.budget ?? 0);
  const [fans, setFans] = useState(slot.fans ?? 0);
  const [week, setWeek] = useState(slot.week ?? 1);
  const [season, setSeason] = useState(slot.season ?? 1);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const Field = ({ label, value, onChange }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div style={{ ...S.mono, fontSize: '0.36rem', letterSpacing: '1px', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase' }}>{label}</div>
      <input type="number" value={value} onChange={e => onChange(e.target.value)}
        style={{ background: faint, border: '1px solid ' + dim, borderRadius: '5px', color: '#fff', fontFamily: "'Anton',sans-serif", fontSize: '0.65rem', padding: '6px 9px', outline: 'none', width: '100%', colorScheme: 'dark' }} />
    </div>
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'gameSlots', slot.id), {
        budget: Number(budget),
        fans: Number(fans),
        week: Number(week),
        season: Number(season),
      });
      setSaved(true);
      onSaved && onSaved({ ...slot, budget: Number(budget), fans: Number(fans), week: Number(week), season: Number(season) });
      setTimeout(() => setSaved(false), 1500);
    } catch (e) {
      alert('Save error: ' + e.message);
    }
    setSaving(false);
  };

  return (
    <div style={{ marginTop: '8px', padding: '12px', borderTop: '1px solid ' + dim, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
      <Field label="Budget" value={budget} onChange={setBudget} />
      <Field label="Fans" value={fans} onChange={setFans} />
      <Field label="Week" value={week} onChange={setWeek} />
      <Field label="Season" value={season} onChange={setSeason} />
      <div style={{ gridColumn: '1 / -1', ...S.mono, fontSize: '0.36rem', color: 'rgba(255,255,255,0.25)', lineHeight: 1.6 }}>
        Rivalites, championships et flow triggers : disponibles uniquement dans le Dev Tools local, depuis la partie.
      </div>
      <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '2px' }}>
        {saved && <span style={{ ...S.mono, fontSize: '0.4rem', color: '#2ecc71', alignSelf: 'center' }}>✓ SAVED</span>}
        <button onClick={handleSave} disabled={saving}
          style={{ ...S.anton, fontSize: '0.46rem', letterSpacing: '1.5px', padding: '6px 16px', borderRadius: '4px', cursor: saving ? 'not-allowed' : 'pointer',
            background: saving ? 'transparent' : 'rgba(212,175,55,0.12)', border: '1px solid ' + (saving ? dim : 'rgba(212,175,55,0.5)'), color: saving ? 'rgba(255,255,255,0.2)' : gold }}>
          {saving ? 'SAVING...' : 'SAVE CHANGES'}
        </button>
      </div>
    </div>
  );
}

// ── Users tab ─────────────────────────────────────────────────────────
function UsersTab() {
  const [allUsers, setAllUsers] = useState(null); // null = loading
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null); // userId currently expanded
  const [slotsByUser, setSlotsByUser] = useState({});
  const [slotsLoading, setSlotsLoading] = useState(null);
  const [openSlot, setOpenSlot] = useState(null); // slotId currently showing editor
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);

  const runUsernameMigration = async () => {
    if (!window.confirm('Backfill the usernames/ index collection from all existing users? This is safe to run multiple times.')) return;
    setMigrating(true);
    setMigrationResult(null);
    try {
      const snap = await getDocs(collection(db, 'users'));
      let created = 0, skipped = 0, invalid = 0;
      const conflicts = [];

      for (const d of snap.docs) {
        const data = d.data();
        const uid = d.id;
        const username = data.username || data.displayName;

        if (!username || !isValidUsernameFormat(username)) { invalid++; continue; }

        const key = username.trim().toLowerCase();
        const ref = doc(db, 'usernames', key);
        const existing = await getDoc(ref);

        if (existing.exists()) {
          if (existing.data().uid !== uid) {
            conflicts.push({ uid, username, existingOwner: existing.data().uid });
          }
          skipped++;
          continue;
        }

        await setDoc(ref, { uid, username: username.trim() });
        created++;
      }

      setMigrationResult({ total: snap.docs.length, created, skipped, invalid, conflicts });
    } catch (e) {
      console.error('Username migration error:', e);
      setMigrationResult({ error: e.message });
    } finally {
      setMigrating(false);
    }
  };

  useEffect(() => {
    getDocs(collection(db, 'users')).then(snap => {
      setAllUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }).catch(e => { console.error('Users fetch error:', e); setAllUsers([]); });
  }, []);

  const toggleUser = async (u) => {
    if (expanded === u.id) { setExpanded(null); return; }
    setExpanded(u.id);
    setOpenSlot(null);
    if (!slotsByUser[u.id]) {
      setSlotsLoading(u.id);
      try {
        const q = query(collection(db, 'gameSlots'), where('userId', '==', u.id));
        const snap = await getDocs(q);
        setSlotsByUser(p => ({ ...p, [u.id]: snap.docs.map(d => ({ id: d.id, ...d.data() })) }));
      } catch (e) {
        console.error('Slots fetch error:', e);
        setSlotsByUser(p => ({ ...p, [u.id]: [] }));
      }
      setSlotsLoading(null);
    }
  };

  const filtered = (allUsers || []).filter(u => {
    const s = search.toLowerCase();
    if (!s) return true;
    return (u.displayName || '').toLowerCase().includes(s) || (u.email || '').toLowerCase().includes(s);
  });

  return (
    <div>
      <div style={{ marginBottom: '14px', padding: '10px 12px', background: 'rgba(212,175,55,0.05)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '6px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ ...S.mono, fontSize: '0.44rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>
            One-off: backfill <code>usernames/</code> index from existing user docs (temporary tool, safe to re-run).
          </div>
          <button onClick={runUsernameMigration} disabled={migrating}
            style={{ flexShrink: 0, ...S.mono, fontSize: '0.44rem', letterSpacing: '1px', background: 'transparent', border: '1px solid ' + gold + '55', color: gold, padding: '6px 14px', borderRadius: '4px', cursor: migrating ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>
            {migrating ? 'RUNNING...' : 'RUN MIGRATION'}
          </button>
        </div>
        {migrationResult && (
          <div style={{ marginTop: '10px', ...S.mono, fontSize: '0.42rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.8 }}>
            {migrationResult.error ? (
              <span style={{ color: '#e74c3c' }}>Error: {migrationResult.error}</span>
            ) : (
              <>
                Total: {migrationResult.total} · Created: {migrationResult.created} · Skipped (already indexed): {migrationResult.skipped} · Invalid format: {migrationResult.invalid}
                {migrationResult.conflicts.length > 0 && (
                  <div style={{ marginTop: '6px', color: '#e74c3c' }}>
                    {migrationResult.conflicts.length} conflict(s) found (duplicate usernames — first one kept the index slot):
                    {migrationResult.conflicts.map((c, i) => (
                      <div key={i} style={{ marginLeft: '10px' }}>- "{c.username}" — uid {c.uid} lost to uid {c.existingOwner}</div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <input type="text" value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Search by name or email..."
        style={{ width: '100%', background: faint, border: '1px solid ' + dim, borderRadius: '6px', color: '#fff', fontFamily: "'Monda',sans-serif", fontSize: '0.55rem', padding: '9px 12px', outline: 'none', marginBottom: '14px' }} />

      {allUsers === null && (
        <div style={{ ...S.mono, fontSize: '0.5rem', color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '24px' }}>LOADING USERS...</div>
      )}

      {allUsers !== null && filtered.length === 0 && (
        <div style={{ ...S.mono, fontSize: '0.5rem', color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '24px' }}>No user matches this search.</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {filtered.map(u => {
          const isOpen = expanded === u.id;
          const slots = slotsByUser[u.id];
          return (
            <div key={u.id} style={{ background: faint, border: '1px solid ' + dim, borderRadius: '8px', overflow: 'hidden' }}>
              <div onClick={() => toggleUser(u)}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', cursor: 'pointer' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Anton',sans-serif", fontSize: '0.5rem', color: gold, flexShrink: 0 }}>
                  {(u.displayName || u.email || '?').slice(0, 2).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ ...S.anton, fontSize: '0.6rem', letterSpacing: '1px', color: '#fff' }}>{u.displayName || '(no display name)'}</div>
                  <div style={{ ...S.mono, fontSize: '0.4rem', color: 'rgba(255,255,255,0.3)' }}>{u.email}</div>
                </div>
                <div style={{ ...S.mono, fontSize: '0.42rem', color: 'rgba(255,255,255,0.25)' }}>{isOpen ? '▲' : '▼'}</div>
              </div>

              {isOpen && (
                <div style={{ borderTop: '1px solid ' + dim, padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {slotsLoading === u.id && (
                    <div style={{ ...S.mono, fontSize: '0.42rem', color: 'rgba(255,255,255,0.3)' }}>Loading slots...</div>
                  )}
                  {slots && slots.length === 0 && (
                    <div style={{ ...S.mono, fontSize: '0.42rem', color: 'rgba(255,255,255,0.3)' }}>No game slots for this user.</div>
                  )}
                  {slots && slots.map(slot => (
                    <div key={slot.id} style={{ border: '1px solid ' + dim, borderRadius: '6px', padding: '8px 10px' }}>
                      <div onClick={() => setOpenSlot(openSlot === slot.id ? null : slot.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                        <span style={{ ...S.anton, fontSize: '0.52rem', letterSpacing: '1px', color: '#fff' }}>{slot.slotName || ('Slot ' + slot.slot)}</span>
                        <span style={{ ...S.mono, fontSize: '0.4rem', color: 'rgba(255,255,255,0.35)' }}>{slot.brand || '—'} · Week {slot.week || 1} · Season {slot.season || 1}</span>
                        <span style={{ ...S.mono, fontSize: '0.4rem', color: gold, marginLeft: 'auto' }}>{openSlot === slot.id ? 'CLOSE' : 'EDIT'}</span>
                      </div>
                      {openSlot === slot.id && (
                        <SlotEditor slot={slot} onSaved={(updated) => {
                          setSlotsByUser(p => ({ ...p, [u.id]: p[u.id].map(s => s.id === updated.id ? updated : s) }));
                        }} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Tickets tab ───────────────────────────────────────────────────────
function TicketsTab() {
  const [tickets, setTickets] = useState(null);
  const [filter, setFilter] = useState('active');
  const [openTicket, setOpenTicket] = useState(null);

  const loadTickets = useCallback(() => {
    const q = query(collection(db, 'tickets'), orderBy('createdAt', 'desc'));
    getDocs(q).then(snap => {
      setTickets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }).catch(e => { console.error('Tickets fetch error:', e); setTickets([]); });
  }, []);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  const changeStatus = async (ticketId, status) => {
    try {
      await updateDoc(doc(db, 'tickets', ticketId), { status, updatedAt: serverTimestamp() });
      setTickets(p => p.map(t => t.id === ticketId ? { ...t, status } : t));
    } catch (e) {
      alert('Could not update status: ' + e.message);
    }
  };

  const deleteTicket = async (ticketId) => {
    if (!window.confirm('Delete this ticket permanently? This cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'tickets', ticketId));
      setTickets(p => p.filter(t => t.id !== ticketId));
      setOpenTicket(null);
    } catch (e) {
      alert('Could not delete ticket: ' + e.message);
    }
  };

  const FILTERS = [
    { value: 'active', label: 'Active' },
    { value: 'all', label: 'All' },
    ...TICKET_STATUSES,
  ];

  const filtered = (tickets || []).filter(t => {
    if (filter === 'all') return true;
    if (filter === 'active') return t.status === 'open' || t.status === 'in_progress';
    return t.status === filter;
  });

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '14px', flexWrap: 'wrap' }}>
        {FILTERS.map(opt => {
          const active = filter === opt.value;
          return (
            <button key={opt.value} onClick={() => setFilter(opt.value)}
              style={{ ...S.mono, fontSize: '0.42rem', letterSpacing: '1px', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer',
                background: active ? 'rgba(212,175,55,0.12)' : faint,
                border: '1px solid ' + (active ? 'rgba(212,175,55,0.5)' : dim),
                color: active ? gold : 'rgba(255,255,255,0.4)' }}>
              {opt.label}
            </button>
          );
        })}
      </div>

      {tickets === null && (
        <div style={{ ...S.mono, fontSize: '0.5rem', color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '24px' }}>LOADING TICKETS...</div>
      )}

      {tickets !== null && filtered.length === 0 && (
        <div style={{ ...S.mono, fontSize: '0.5rem', color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '24px' }}>No ticket in this status.</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {filtered.map(t => {
          const meta = TICKET_STATUS_MAP[t.status] || TICKET_STATUS_MAP.open;
          const isOpen = openTicket === t.id;
          return (
            <div key={t.id} style={{ background: faint, border: '1px solid ' + dim, borderRadius: '8px', overflow: 'hidden' }}>
              <div onClick={() => setOpenTicket(isOpen ? null : t.id)}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', cursor: 'pointer' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ ...S.anton, fontSize: '0.56rem', letterSpacing: '0.5px', color: '#fff' }}>{t.title}</div>
                  <div style={{ ...S.mono, fontSize: '0.38rem', color: 'rgba(255,255,255,0.3)', marginTop: '2px' }}>
                    {TICKET_CATEGORY_LABELS[t.category] || t.category} · {t.displayName || 'Unknown'} · {formatDate(t.createdAt)}
                  </div>
                </div>
                <span style={{ ...S.mono, fontSize: '0.4rem', fontWeight: 'bold', letterSpacing: '0.5px', padding: '3px 10px', borderRadius: '10px', color: meta.color, border: '1px solid ' + meta.color + '55', background: meta.color + '18', whiteSpace: 'nowrap' }}>
                  {meta.label}
                </span>
              </div>

              {isOpen && (
                <div style={{ borderTop: '1px solid ' + dim, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div>
                    <div style={{ ...S.mono, fontSize: '0.36rem', letterSpacing: '1px', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', marginBottom: '4px' }}>Description</div>
                    <div style={{ ...S.mono, fontSize: '0.5rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{t.description}</div>
                  </div>
                  {t.stepsToReproduce && (
                    <div>
                      <div style={{ ...S.mono, fontSize: '0.36rem', letterSpacing: '1px', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', marginBottom: '4px' }}>Steps to reproduce</div>
                      <div style={{ ...S.mono, fontSize: '0.5rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{t.stepsToReproduce}</div>
                    </div>
                  )}
                  <div style={{ ...S.mono, fontSize: '0.4rem', color: 'rgba(255,255,255,0.3)' }}>
                    Browser & device: {t.browserDevice || 'Unknown'}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px', alignItems: 'center' }}>
                    {TICKET_STATUSES.map(opt => (
                      <button key={opt.value} onClick={() => changeStatus(t.id, opt.value)}
                        disabled={t.status === opt.value}
                        style={{ ...S.mono, fontSize: '0.4rem', letterSpacing: '0.5px', padding: '6px 12px', borderRadius: '4px',
                          cursor: t.status === opt.value ? 'default' : 'pointer',
                          background: t.status === opt.value ? opt.color + '22' : 'transparent',
                          border: '1px solid ' + (t.status === opt.value ? opt.color : dim),
                          color: t.status === opt.value ? opt.color : 'rgba(255,255,255,0.4)' }}>
                        {opt.label}
                      </button>
                    ))}
                    {(t.status === 'resolved' || t.status === 'wont_fix') && (
                      <button onClick={() => deleteTicket(t.id)}
                        style={{ ...S.mono, fontSize: '0.4rem', letterSpacing: '0.5px', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer',
                          background: 'transparent', border: '1px solid rgba(231,76,60,0.4)', color: 'rgba(231,76,60,0.8)', marginLeft: 'auto' }}>
                        DELETE TICKET
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────

// ── Modals tab ────────────────────────────────────────────────────────
function ModalsTab({ onLaunchWelcome, onLaunchPatchNotes }) {
  const Row = ({ color = gold, label, desc, onClick, btnLabel }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', padding: '14px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
      <div>
        <div style={{ ...S.mono, fontSize: '0.55rem', color: 'rgba(255,255,255,0.75)', letterSpacing: '0.5px', marginBottom: '4px' }}>{label}</div>
        <div style={{ ...S.mono, fontSize: '0.48rem', color: 'rgba(255,255,255,0.3)', lineHeight: 1.6 }}>{desc}</div>
      </div>
      <button onClick={onClick}
        style={{ flexShrink: 0, ...S.mono, fontSize: '0.46rem', letterSpacing: '1.5px', textTransform: 'uppercase', background: 'transparent', border: '1px solid ' + color + '55', color: color, padding: '7px 16px', borderRadius: '4px', cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap' }}
        onMouseEnter={e => { e.currentTarget.style.background = color + '18'; e.currentTarget.style.borderColor = color; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = color + '55'; }}>
        {btnLabel}
      </button>
    </div>
  );

  return (
    <div>
      <div style={{ ...S.mono, fontSize: '0.44rem', letterSpacing: '2px', color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase', marginBottom: '16px', paddingBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        MODAL TRIGGERS — Preview & test onboarding flows
      </div>
      <Row
        color={gold}
        label="Welcome Modal (Tour + Patch Notes)"
        desc="Opens the full first-login experience: Quick Tour slides followed by Patch Notes. Simulates what a brand new user sees."
        onClick={onLaunchWelcome}
        btnLabel="Launch Welcome →"
      />
      <Row
        color="#e01010"
        label="Patch Notes Only"
        desc="Opens the Patch Notes modal in isolation, without the Quick Tour tab. Simulates what a returning user sees after an update."
        onClick={onLaunchPatchNotes}
        btnLabel="Launch Patch Notes →"
      />
      <div style={{ marginTop: '20px', padding: '12px 14px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px' }}>
        <div style={{ ...S.mono, fontSize: '0.44rem', color: 'rgba(255,255,255,0.2)', lineHeight: 1.7 }}>
          These triggers are for preview only and do not modify Firestore flags.<br />
          Closing the modal will not mark it as seen for your account.
        </div>
      </div>
    </div>
  );
}

export default function AdminPanel({ onClose, onLaunchWelcome, onLaunchPatchNotes }) {
  const [user] = useAuthState(auth);
  const accessStatus = useAccess(user, false);
  const [tab, setTab] = useState('users');

  if (accessStatus !== 'admin') return null;

  const tabs = [
    { key: 'users', label: 'USERS' },
    { key: 'tickets', label: 'TICKETS' },
    { key: 'modals', label: 'MODALS' },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(12px)' }}
      onClick={onClose}>
      <div style={{ background: 'rgba(6,6,6,0.98)', border: '1px solid rgba(212,175,55,0.3)', borderRadius: '16px', width: '100%', maxWidth: '720px', maxHeight: '88vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '20px 24px 0', position: 'sticky', top: 0, background: 'rgba(6,6,6,0.98)', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{ ...S.anton, fontSize: '0.9rem', letterSpacing: '3px', color: gold }}>ADMIN PANEL</div>
            <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem' }}>✕</button>
          </div>
          <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid rgba(212,175,55,0.15)' }}>
            {tabs.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                style={{ ...S.mono, fontSize: '0.5rem', letterSpacing: '1.5px', background: 'transparent', border: 'none', borderBottom: '2px solid ' + (tab === t.key ? gold : 'transparent'), color: tab === t.key ? gold : 'rgba(255,255,255,0.35)', padding: '8px 18px', cursor: 'pointer' }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ padding: '18px 24px 24px' }}>
          {tab === 'users' && <UsersTab />}
          {tab === 'tickets' && <TicketsTab />}
          {tab === 'modals' && (
            <ModalsTab
              onLaunchWelcome={() => { onClose(); onLaunchWelcome && onLaunchWelcome(); }}
              onLaunchPatchNotes={() => { onClose(); onLaunchPatchNotes && onLaunchPatchNotes(); }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
